// Therapist UI design tokens.
//
// Introduced in Phase 1 (ticket 1.7) to route status colours through a single
// source instead of ~40 inline hex literals. Promoted to canonical in Phase 3
// (ticket 3.1), which codemods the rest of the therapist tree onto it.
//
// IMPORTANT (zero-diff migration): the `status.*.solid` values and `chartSeries`
// below intentionally match the colours the dashboard already uses, so adopting
// these tokens changes nothing visually. The `bg`/`fg` chip pairs are the
// accessible (WCAG-checked) pairs reserved for the Phase 3 design-system pass.

export const tokens = {
  color: {
    bg:     { canvas: '#F5F7FB', surface: '#FFFFFF' },
    border: { subtle: '#E5E7EB', strong: '#D1D5DB' },
    text:   { primary: '#111827', secondary: '#6B7280', muted: '#9CA3AF', inverse: '#FFFFFF' },
    brand:  { 500: '#1976D2', 600: '#1565C0' },   // primary
    accent: { 500: '#FF8C00', 600: '#E07B00' },   // active state (non-text; see a11y note)
    status: {
      // solid = current in-app colour (zero-diff); bg/fg = accessible chip pair (Phase 3)
      critical: { bg: '#FEE2E2', fg: '#B91C1C', solid: '#EF5350' },
      high:     { bg: '#FFEDD5', fg: '#9A3412', solid: '#F97316' },
      moderate: { bg: '#FEF3C7', fg: '#92400E', solid: '#FBBF24' },
      routine:  { bg: '#DCFCE7', fg: '#166534', solid: '#66BB6A' },
      info:     { bg: '#DBEAFE', fg: '#1D4ED8', solid: '#42A5F5' },
      neutral:  { bg: '#F3F4F6', fg: '#374151', solid: '#9CA3AF' },
    },
  },
  // Recharts line/series palette (kept as an ordered array, matching current usage).
  chartSeries: ['#EF5350', '#66BB6A', '#42A5F5', '#FF8C00', '#9C27B0'],
  radius: { sm: 6, md: 8, lg: 12, pill: 9999 },
  space:  { 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40 },
  type: {
    h1:    { size: 22, weight: 700, lh: 1.3 },
    h2:    { size: 18, weight: 600, lh: 1.35 },
    h3:    { size: 15, weight: 600, lh: 1.4 },
    body:  { size: 13, weight: 400, lh: 1.5 },
    label: { size: 11, weight: 600, lh: 1.4, tracking: 0.6, transform: 'uppercase' },
  },
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,0.04)',
    md: '0 4px 12px rgba(15,23,42,0.06)',
    lg: '0 12px 32px rgba(15,23,42,0.10)',
  },
};

// Resolve a tier name ('critical' | 'high' | 'moderate' | 'routine' | 'info')
// to its colour set; unknown tiers fall back to neutral.
export const statusColor = (tier) =>
  tokens.color.status[tier] ?? tokens.color.status.neutral;

export default tokens;
